/**
 * SVG scale + path helpers (TASK-20260823-twap-strategies-tca, PR-A2).
 *
 * The TWAP report charts (savings waterfall, execution tape, pace chart) are inline SVG so their
 * marks are DOM nodes: focusable, labelled, themed by CSS tokens, and testable without a canvas.
 * `chartUtils.js` stays Canvas-only for the candle chart. Everything here is pure arithmetic —
 * no DOM, no Svelte — so the chart components stay thin.
 */

const round2 = (v) => Math.round(v * 100) / 100;

const isFinite_ = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * A linear scale: `domain` → `range`. Returns a function with `.invert`, `.ticks(n)`, `.domain`,
 * `.range`. A zero-width domain maps every input to the range midpoint (renders a flat line rather
 * than NaN). `clamp: true` pins out-of-domain inputs to the range ends.
 */
export function linearScale([d0, d1], [r0, r1], { clamp = false } = {}) {
  const dw = d1 - d0;
  const rw = r1 - r0;
  const scale = (v) => {
    if (!isFinite_(v)) return null;
    if (dw === 0) return r0 + rw / 2;
    let t = (v - d0) / dw;
    if (clamp) t = Math.min(1, Math.max(0, t));
    return r0 + t * rw;
  };
  scale.invert = (r) => (rw === 0 ? d0 : d0 + ((r - r0) / rw) * dw);
  scale.ticks = (n = 5) => niceTicks(d0, d1, n);
  scale.domain = [d0, d1];
  scale.range = [r0, r1];
  return scale;
}

/** Coerce an ISO string / Date / epoch-ms into epoch ms, or null when unparseable. */
export function toMs(t) {
  if (t == null) return null;
  if (t instanceof Date) return Number.isNaN(t.getTime()) ? null : t.getTime();
  if (typeof t === 'number') return Number.isFinite(t) ? t : null;
  const ms = Date.parse(t);
  return Number.isNaN(ms) ? null : ms;
}

const MINUTE = 60_000;
const TIME_STEPS_MS = [
  MINUTE, 2 * MINUTE, 5 * MINUTE, 10 * MINUTE, 15 * MINUTE, 30 * MINUTE,
  60 * MINUTE, 2 * 60 * MINUTE, 3 * 60 * MINUTE, 6 * 60 * MINUTE, 12 * 60 * MINUTE, 24 * 60 * MINUTE,
];

/**
 * A time scale over epoch-ms (inputs may be ISO strings or Dates). `.ticks(n)` returns whole-minute
 * (or coarser round-interval) instants inside the domain.
 */
export function timeScale(domain, range, opts) {
  const [d0, d1] = domain.map(toMs);
  const base = linearScale([d0, d1], range, opts);
  const scale = (t) => base(toMs(t));
  scale.invert = base.invert;
  scale.domain = [d0, d1];
  scale.range = range;
  scale.ticks = (n = 5) => {
    const span = d1 - d0;
    if (!(span > 0)) return [d0];
    const target = span / Math.max(1, n);
    const step = TIME_STEPS_MS.find((s) => s >= target) ?? TIME_STEPS_MS[TIME_STEPS_MS.length - 1];
    const ticks = [];
    for (let t = Math.ceil(d0 / step) * step; t <= d1; t += step) ticks.push(t);
    return ticks;
  };
  return scale;
}

function tickStep(lo, hi, count) {
  const span = hi - lo;
  const rough = span / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(rough));
  const norm = rough / mag;
  // d3-style thresholds: pick the nearest of 1/2/5/10 × magnitude.
  const nice = norm >= 7.5 ? 10 : norm >= 3.5 ? 5 : norm >= 1.5 ? 2 : 1;
  return nice * mag;
}

/** Round tick values covering [lo, hi] (inclusive of the enclosing round numbers). */
export function niceTicks(lo, hi, count = 5) {
  if (!isFinite_(lo) || !isFinite_(hi)) return [];
  if (lo === hi) return [lo];
  if (lo > hi) [lo, hi] = [hi, lo];
  const step = tickStep(lo, hi, count);
  const start = Math.floor(lo / step) * step;
  const end = Math.ceil(hi / step) * step;
  const ticks = [];
  const decimals = Math.max(0, -Math.floor(Math.log10(step)));
  for (let v = start; v <= end + step / 2; v += step) ticks.push(Number(v.toFixed(decimals)));
  return ticks;
}

/** The enclosing round domain for [lo, hi]. */
export function niceDomain(lo, hi, count = 5) {
  const ticks = niceTicks(lo, hi, count);
  if (ticks.length === 0) return [lo, hi];
  return [ticks[0], ticks[ticks.length - 1]];
}

/** [min, max] over the finite values; [null, null] when none. */
export function extent(values) {
  let lo = null;
  let hi = null;
  for (const v of values ?? []) {
    if (!isFinite_(v)) continue;
    if (lo === null || v < lo) lo = v;
    if (hi === null || v > hi) hi = v;
  }
  return [lo, hi];
}

/** Widen a domain by `fraction` of its width on each side (by one unit when the width is zero). */
export function pad([lo, hi], fraction = 0.1) {
  const w = hi - lo;
  const p = w === 0 ? 1 : w * fraction;
  return [lo - p, hi + p];
}

function pt(x, y) {
  return `${round2(x)},${round2(y)}`;
}

/** `M…L…` polyline; a null/NaN coordinate breaks the line (a new `M` at the next drawable point). */
export function linePath(points, x, y) {
  let d = '';
  let pen = false;
  for (const p of points ?? []) {
    const px = x(p);
    const py = y(p);
    if (!isFinite_(px) || !isFinite_(py)) { pen = false; continue; }
    d += `${pen ? 'L' : 'M'}${pt(px, py)}`;
    pen = true;
  }
  return d;
}

/**
 * A closed area: the upper edge `y1` forward, then the lower edge `y0` back. With a constant `y0`
 * this is a classic area-to-baseline; with a per-point `y0` it is a band between two series.
 * Points with a non-finite coordinate are skipped.
 */
export function areaPath(points, x, y1, y0) {
  const usable = (points ?? []).filter((p) => isFinite_(x(p)) && isFinite_(y1(p)) && isFinite_(y0(p)));
  if (usable.length === 0) return '';
  let d = '';
  usable.forEach((p, i) => { d += `${i === 0 ? 'M' : 'L'}${pt(x(p), y1(p))}`; });
  for (let i = usable.length - 1; i >= 0; i--) d += `L${pt(x(usable[i]), y0(usable[i]))}`;
  return `${d}Z`;
}

/** A step-after path: each value holds until the next x. */
export function stepPath(points, x, y) {
  const usable = (points ?? []).filter((p) => isFinite_(x(p)) && isFinite_(y(p)));
  if (usable.length === 0) return '';
  let d = `M${pt(x(usable[0]), y(usable[0]))}`;
  for (let i = 1; i < usable.length; i++) {
    d += `L${pt(x(usable[i]), y(usable[i - 1]))}L${pt(x(usable[i]), y(usable[i]))}`;
  }
  return d;
}

/** Short tick label for a number (2 significant decimals at most, thousands separated). */
export function formatTick(v) {
  if (!isFinite_(v)) return '';
  const abs = Math.abs(v);
  const decimals = abs >= 1000 ? 0 : abs >= 10 ? 1 : 2;
  return v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

/** HH:MM (UTC-agnostic local time) label for a time tick. */
export function formatTimeTick(ms) {
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}
